import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaPlus as PlusIcon, FaExclamationCircle as ExclCircle } from 'react-icons/fa';

import CustomNumeral from '../../shared/customNumeral.component';
import { NumeralTypes } from '../../shared/numeral.component';
import { EffectiveTaxRateReportPropTypes } from './propTypes';
import EditableWrapper from './groupData/editableWrapper.component';
import GroupData from './groupData/groupData.component';
import { SortOrders, Sections } from './constants';
import ConfirmDeletingModal from './confirmDeletingModal';
import EditableRow from './editableSubrow.component';
import { totalNegativePercentage } from './utils/numeral.utils';
import { UserWithoutActiveFlagPropTypes } from '../../shared/propTypes/users';
import Spinner from '../../shared/spinner.component';
import GroupingColumns from './GroupingColumns.component';
import EffectiveTaxRateDetails from './effectiveTaxRateDetails.component';

import { FUNCTIONAL_CURRENCY } from '@common/constants/sharedConstants';

const renderColumnsHeadersRow = ({
  groupingColumns = [],
  totalColumnName,
  noReportData,
}) => (
  noReportData
    ? (
      <div className="d-flex align-items-center amount-info-wrapper">
        <ExclCircle size={20} />
        No calc data for selected parameters
      </div>
    )
    : (
      <div className="row parent-row header-row no-margin stick-top">
        <div className="etr-icon-wrapper stick-left corner-cover" />
        <div className="col-6 description stick-left corner-cover" />
        <div className="col-2 text-center etr-column-header">
          <div className="header-row-cell">{ totalColumnName }</div>
        </div>
        <div className="col-2 text-center etr-column-header">
          <div className="header-row-cell">Percentage</div>
        </div>
        {
          groupingColumns && groupingColumns.map(columnHeader => (
            <div key={columnHeader} className="col-2 text-center etr-column-header">
              <div className="header-row-cell">{ columnHeader }</div>
            </div>
          ))
        }
      </div>
    )
);

renderColumnsHeadersRow.propTypes = {
  groupingColumns: PropTypes.arrayOf(PropTypes.string.isRequired),
  totalColumnName: PropTypes.string,
  noReportData: PropTypes.bool,
};

const renderFirstRows = (firstRows, suppressHidingDecimalPlacesForPercentage, hideGroupingColumns) => (
  firstRows.map(row => {
    const parentRowClassNames = classnames('row parent-row', {
      'no-margin': row.sortOrder === SortOrders.INCOME_AT_SELECTED_RATE,
    });

    if (row.sortOrder !== SortOrders.TAX_RATE) {
      return (
        <div className={parentRowClassNames} key={row.sortOrder}>
          <div className="etr-icon-wrapper stick-left" />
          <div className="col-6 description font-weight-bold stick-left">{row.descr}</div>
          <div className="col-2 text-right font-weight-bold">
            <CustomNumeral type={NumeralTypes.DECIMAL}>{row.amount}</CustomNumeral>
          </div>
          <div className="col-2" />
          <GroupingColumns
            hideGroupingColumns={hideGroupingColumns}
            columnsData={row.columnsData}
          />
        </div>
      );
    }

    if (row.sortOrder === SortOrders.TAX_RATE) {
      return (
        <div className="row child-row" key={row.sortOrder}>
          <div className="etr-icon-wrapper stick-left" />
          <div className="col-6 description stick-left">{row.descr}</div>
          <div className="col-2 text-right font-weight-bold" />
          <div className="col-2 text-right font-weight-bold">
            <CustomNumeral
              additionalClassNames={totalNegativePercentage}
              type={NumeralTypes.PERCENT_4}
              suppressHidingDecimalPlaces={suppressHidingDecimalPlacesForPercentage}
            >
              {row.amount}
            </CustomNumeral>
          </div>
        </div>
      );
    }

    return null;
  })
);

const renderSummaryRows = (summaryRows, suppressHidingDecimalPlacesForPercentage, hideGroupingColumns) => (
  summaryRows.map((row, index) => {
    const borderTopClassNames = classnames({
      'double-border-top': index === 0,
    });
    const descriptionClassNames = classnames('col-6', 'description', 'no-margin', 'font-weight-bold', 'stick-left', borderTopClassNames);
    const amountClassNames = classnames('col-2', 'text-right', 'no-margin', 'font-weight-bold', borderTopClassNames);
    const taxRateClassNames = classnames('col-2', 'text-right', 'no-margin', 'font-weight-bold', borderTopClassNames);
    const groupingColumnsClassNames = classnames('col-2', 'text-right', borderTopClassNames);

    const groupingColumnsNumeralType = row.section === Sections.EFFECTIVE_TAX_RATE
      ? NumeralTypes.PERCENT_4
      : NumeralTypes.DECIMAL;

    return (
      <div className="row parent-row" key={row.section}>
        <div className="etr-icon-wrapper stick-left" />
        <div className={descriptionClassNames}>{row.descr}</div>
        <div className={amountClassNames}>
          {row.section === Sections.PROVISION_FOR_INCOME_TAXES
            ? <CustomNumeral type={NumeralTypes.DECIMAL}>{row.amount}</CustomNumeral>
            : null
          }
        </div>
        <div className={taxRateClassNames}>
          {row.section === Sections.EFFECTIVE_TAX_RATE
            ? (
              <CustomNumeral
                additionalClassNames={totalNegativePercentage}
                type={NumeralTypes.PERCENT_4}
                suppressHidingDecimalPlaces={suppressHidingDecimalPlacesForPercentage}
              >
                {row.amount}
              </CustomNumeral>)
            : null
          }
        </div>
        {
          !hideGroupingColumns && row.columnsData && row.columnsData.map((columnAmount, index) => (
            <div key={index} className={groupingColumnsClassNames}>
              { columnAmount && <CustomNumeral type={groupingColumnsNumeralType}>{columnAmount}</CustomNumeral> }
            </div>
          ))
        }
      </div>
    );
  })
);

class EffectiveTaxRateReport extends PureComponent {
  static propTypes = {
    reportData: EffectiveTaxRateReportPropTypes,
    fetchReport: PropTypes.func.isRequired,
    confirmDeletingDriverModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        rowData: PropTypes.shape({
          companyCode: PropTypes.string.isRequired,
          countryTaxType: PropTypes.string.isRequired,
          tagName: PropTypes.string.isRequired,
        }).isRequired,
      }),
    }).isRequired,
    showDeletingDriverModal: PropTypes.func.isRequired,
    user: UserWithoutActiveFlagPropTypes.isRequired,
    isInCurrentPeriod: PropTypes.bool.isRequired,
    reportingCurrencyCode: PropTypes.string.isRequired,
    suppressHidingDecimalPlacesForPercentage: PropTypes.bool,
    isLoading: PropTypes.bool,
    hideGroupingColumns: PropTypes.bool,
    totalColumnName: PropTypes.string,
    groupingColumns: PropTypes.arrayOf(PropTypes.string.isRequired),
    hasAnyCompanyCodesForManagingETRDrivers: PropTypes.func.isRequired,
    displayGroupingColumnsByPercentages: PropTypes.bool.isRequired,
    toggleVisibility: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isLoading: false,
    hideGroupingColumns: false,
    totalColumnName: 'Total',
    groupingColumns: [],
  };

  state = {
    addingNewRow: false,
    scroll: { left: 0, top: 0 },
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (!this.props.isInCurrentPeriod) {
      this.setState({
        addingNewRow: false,
      });
    }
    if (prevState.addingNewRow !== this.state.addingNewRow) {
      this.updateStickyElementsPosition();
    }

    if (!this.shouldShowAddRateDriver()) {
      this.closeNewRow();
    }
  };

  shouldShowAddRateDriver = () => {
    const {
      isInCurrentPeriod,
      reportingCurrencyCode,
    } = this.props;

    const isFunctionalCurrencySelected = reportingCurrencyCode === FUNCTIONAL_CURRENCY;
    const showAddRateDriver = isFunctionalCurrencySelected && isInCurrentPeriod;
    return showAddRateDriver;
  };

  reportRef = null;

  setReportRef = node => this.reportRef = node;

  onReportScroll = e => {
    if (e.target === this.reportRef) {
      this.setState({
        scroll: { left: e.target.scrollLeft, top: e.target.scrollTop },
      }, this.updateStickyElementsPosition);
    }
  };

  updateStickyElementsPosition = () => {
    const { left, top } = this.state.scroll;
    document.querySelectorAll('.effective-tax-rate-report .stick-left').forEach($element => {
      $element.style.left = `${left}px`;
    });
    document.querySelectorAll('.effective-tax-rate-report .stick-top').forEach($element => {
      $element.style.top = `${top}px`;
    });
  };

  addNewRowForNewTag = event => {
    event.preventDefault();
    this.setState(({ addingNewRow }) => ({
      addingNewRow: !addingNewRow,
    }));
  };

  closeNewRowAndFetchReport = async () => {
    await this.props.fetchReport();
    this.setState({ addingNewRow: false });
  };

  closeNewRow = () => {
    this.setState({ addingNewRow: false });
  };

  renderReport = () => {
    const {
      reportData,
      fetchReport,
      showDeletingDriverModal,
      user,
      suppressHidingDecimalPlacesForPercentage,
      isLoading,
      hideGroupingColumns,
      totalColumnName,
      groupingColumns,
      hasAnyCompanyCodesForManagingETRDrivers,
      displayGroupingColumnsByPercentages,
      toggleVisibility,
    } = this.props;

    const {
      addingNewRow,
    } = this.state;

    const {
      beginningRows: firstRows,
      summaryRows,
    } = reportData;

    const ibtRow = firstRows.find(row => row.sortOrder === SortOrders.INCOME_BEFORE_TAX);
    const ibtAmount = ibtRow ? ibtRow.amount : 0;

    const showAddRateDriver = this.shouldShowAddRateDriver();

    const rowForNewTagValues = !addingNewRow && hasAnyCompanyCodesForManagingETRDrivers() && (
      <div className="row parent-row font-weight-bold add-new-row stick-left">
        <div className="col-4">
          <PlusIcon className="icon-plus" size={16} onClick={this.addNewRowForNewTag} />
        </div>
      </div>
    );

    const wrapperClassName = classnames(
      'effective-tax-rate-report container-fluid mt-5 mb-5',
      { hidden: isLoading }
    );

    const columnHeadersOptions = {
      totalColumnName,
      groupingColumns: hideGroupingColumns ? [] : groupingColumns,
      noReportData: !reportData.beginningRows.length,
    };
    return (
      <div className={wrapperClassName} onScroll={this.onReportScroll} ref={this.setReportRef}>
        {renderColumnsHeadersRow(columnHeadersOptions)}
        {renderFirstRows(firstRows, suppressHidingDecimalPlacesForPercentage, hideGroupingColumns)}
        {reportData.tagsGroups.map(({
          tagName,
          tagDescription,
          subrows,
          columnsData,
          isOpen,
        }, index) => (
          <GroupData
            key={tagName}
            tagName={tagName}
            tagDescription={tagDescription}
            subrows={subrows}
            columnsData={columnsData}
            ibtAmount={ibtAmount}
            fetchReport={fetchReport}
            showDeletingDriverModal={showDeletingDriverModal}
            user={user}
            showAddRateDriver={showAddRateDriver}
            suppressHidingDecimalPlacesForPercentage={suppressHidingDecimalPlacesForPercentage}
            hideGroupingColumns={hideGroupingColumns}
            hasAnyCompanyCodesForManagingETRDrivers={hasAnyCompanyCodesForManagingETRDrivers}
            updateStickyElementsPosition={this.updateStickyElementsPosition}
            displayGroupingColumnsByPercentages={displayGroupingColumnsByPercentages}
            toggleVisibility={toggleVisibility}
            isOpen={isOpen}
            path={['tagsGroups', index]}
          />
        ))}
        {showAddRateDriver && rowForNewTagValues}
        {addingNewRow && (
          <EditableWrapper>
            {changeEditable => (
              <EditableRow
                closeAndFetchReport={this.closeNewRowAndFetchReport}
                close={this.closeNewRow}
                changeEditableOnParent={changeEditable}
              />
            )}
          </EditableWrapper>
        )}
        {renderSummaryRows(summaryRows, suppressHidingDecimalPlacesForPercentage, hideGroupingColumns)}
        <EffectiveTaxRateDetails />
      </div>
    );
  };

  render() {
    const {
      isLoading,
      reportData,
      confirmDeletingDriverModalData,
      fetchReport,
    } = this.props;

    return (
      <div>
        { isLoading && <div className="spinner-wrapper"><Spinner /></div> }
        { reportData && this.renderReport() }
        <ConfirmDeletingModal
          data={confirmDeletingDriverModalData.data}
          fetchReport={fetchReport}
          isVisible={confirmDeletingDriverModalData.show}
        />
      </div>
    );
  }
}

export default EffectiveTaxRateReport;
